// Testing
import React, { useState, useEffect, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';

import API from '../../../services/api';
import { UserContext } from '../../../utils/contexts/UserContext';
import AccountInfo from './AccountInfo';
import PersonalDetails from './PersonalDetails';
import ContactInfo from './ContactInfo';
import Bio from './Bio';
import SecurityTab from './Security';
import AvatarEditor from './AvatarEditor';
import { getCountries, getGenders } from '../../../services/Auth/authService';
import { useLocation } from 'react-router-dom';

// Create a service function for updating user profile
const updateUserProfile = async (userData) => {
  try {
    const response = await API.patch(`/auth/users/${userData.uuid}/`, userData);
    return response.data;
  } catch (error) {
    console.error('Failed to update profile:', error);
    throw error;
  }
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const EditProfile = () => {
  const { user, setUser } = useContext(UserContext);
  const canManageAvatar = user?.roles?.some((role) => ['STD', 'EXP'].includes(role.code));
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [genders, setGenders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [tab, setTab] = useState(0);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Load user data and genders when component mounts
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const requests = [
      getGenders({ signal }),
      getCountries({ params: { page_size: 500 }, signal }),
    ];
    Promise.all(requests)
      .then(([genderRes, countryRes]) => {
        const genderList = genderRes.results.map((item) => ({
          value: item.uuid,
          label: item.name,
        }));
        const countryList = countryRes.results.map((item) => ({
          value: item.uuid,
          label: item.name,
        }));
        const nationalityList = countryRes.results.map((item) => ({
          value: item.name,
          label: item.name,
        }));
        setGenders(genderList);
        setCountries(countryList);
        setNationalities(nationalityList);
      })
      .catch((error) => {
        console.error('Gender fetch failed', error);
      })
      .finally(() => {
        if (signal.aborted) return;
        setIsLoading(false);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (state?.tab) {
      if (state.tab === 'contact-info') {
        setTab(canManageAvatar ? 3 : 2);
      }
    }
  }, [canManageAvatar, state]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const updatedUser = await updateUserProfile({ ...data, uuid: user.uuid });
      setUser((prevUser) => ({ ...prevUser, ...updatedUser }));
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const tabCategories = {
    ...(canManageAvatar ? { 'Profile Photo': <AvatarEditor /> } : {}),
    'Account Info': (
      <AccountInfo register={register} errors={errors} user={user} />
    ),
    'Personal Details': (
      <PersonalDetails
        control={control}
        register={register}
        errors={errors}
        genders={genders}
        nationalities={nationalities}
        user={user}
      />
    ),
    'Contact Info': (
      <ContactInfo
        register={register}
        control={control}
        errors={errors}
        user={user}
        countries={countries}
      />
    ),
    About: <Bio register={register} user={user} />,
    Security: <SecurityTab user={user} />,
  };

  return (
    <div className="min-h-screen">
      <div className="w-full lg:max-w-2xl xl:max-w-4xl mx-auto py-4">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          Edit Profile
        </h2>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <TabGroup selectedIndex={tab} onChange={setTab}>
              <TabList className="flex flex-wrap sm:flex-nowrap space-x-1 rounded-xl bg-blue-900/20 p-1">
                {Object.keys(tabCategories).map((category) => (
                  <Tab
                    key={category}
                    className={({ selected }) =>
                      classNames(
                        'w-full rounded-lg py-2.5 text-xs sm:text-sm font-medium leading-5 ',
                        'ring-white/60 focus:outline-none',
                        selected
                          ? 'bg-white text-blue-700 shadow'
                          : 'text-gray-500 dark:text-gray-100 hover:bg-gray-200 hover:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-white',
                      )
                    }
                  >
                    {category}
                  </Tab>
                ))}
              </TabList>
              <TabPanels className="mt-2">
                {Object.values(tabCategories).map((content, idx) => (
                  <TabPanel
                    key={idx}
                    className={classNames(
                      'rounded-xl bg-white dark:bg-gray-800 p-2 lg:p-3',
                      'ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                    )}
                  >
                    {content}
                  </TabPanel>
                ))}
              </TabPanels>
            </TabGroup>

            {/* Submit Button */}
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="py-2 px-6 bg-blue-950 text-white rounded-xl hover:opacity-80 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
